import { useState } from 'react';
import { Sparkles, RefreshCw, ExternalLink, Search, ShoppingBag } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Badge, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatCurrency } from '@/lib/utils';
import type { AiSuggestion } from '@/lib/types';

function mrPorterUrl(query: string): string {
  return `https://www.mrporter.com/en-us/mens/search?q=${encodeURIComponent(query)}`;
}

function googleShoppingUrl(query: string): string {
  return `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(query)}`;
}

export function AiSuggestionsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  // First load: plain fetch. Each refresh appends ?refresh=1 and a cache-busting
  // counter so useApi (keyed on the path string) issues a fresh request.
  const path =
    refreshKey > 0 ? `/ai/suggestions?refresh=1&_=${refreshKey}` : '/ai/suggestions';
  const { data, loading, error } = useApi<{ suggestions: AiSuggestion[] }>(path);

  const suggestions = data?.suggestions ?? [];

  return (
    <Page>
      <PageHeader section="AI Suggestions" backHref="/before-buy" backLabel="Before You Buy" />
      <Main className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-2 flex-1 mr-3">
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Personalized picks based on your wardrobe gaps, usage patterns, and style.
            </p>
          </div>
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
            className="p-2.5 bg-secondary rounded-xl hover:bg-muted transition-all disabled:opacity-50 flex-shrink-0"
            aria-label="Refresh suggestions"
          >
            <RefreshCw className={`w-5 h-5 text-foreground ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading && <LoadingState label="Asking Claude for recommendations…" />}
        {error && <ErrorState message={error} />}

        {!loading && !error && suggestions.length === 0 && (
          <EmptyState
            icon={<ShoppingBag className="w-8 h-8" />}
            title="No suggestions"
            description="Refresh to generate new recommendations based on your wardrobe."
            action={
              <Button onClick={() => setRefreshKey((k) => k + 1)}>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            }
          />
        )}

        {!loading && !error && suggestions.length > 0 && (
          <div className="space-y-4">
            {suggestions.map((s, i) => (
              <SuggestionCard key={`${s.brand}-${s.name}-${i}`} suggestion={s} />
            ))}
          </div>
        )}
      </Main>
    </Page>
  );
}

function SuggestionCard({ suggestion }: { suggestion: AiSuggestion }) {
  const query = `${suggestion.brand} ${suggestion.name}`.trim();
  const confidencePct = Math.round(suggestion.confidence * 100);

  return (
    <Card className="overflow-hidden">
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground">{suggestion.name}</h3>
              {suggestion.fillsGap && <Badge tone="success">Fills Gap</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {suggestion.brand}
              {suggestion.category ? ` · ${suggestion.category}` : ''}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            {suggestion.estimatedPrice != null && (
              <p className="text-base font-bold text-foreground">{formatCurrency(suggestion.estimatedPrice)}</p>
            )}
            <p className="text-[10px] text-muted-foreground">est.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge tone="accent">{confidencePct}% match</Badge>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">{suggestion.matchReason}</p>

        <div className="flex flex-wrap gap-2 pt-1">
          <a
            href={mrPorterUrl(query)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            Mr Porter
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href={googleShoppingUrl(query)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            Google Shopping
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </Card>
  );
}
